import React from 'react';
import { ContextCode } from './ContextCode';

interface OptionItem {
    id: string;
    content_type: 'text' | 'code';
    value: string;
}

interface QuestionMCQMultiProps {
    question: {
        id: string;
        type: string;
        prompt: string;
        content: {
            context_code?: string;
            options: OptionItem[];
            /** Nombre de réponses attendues (« Deux réponses ») : au-delà, une case de plus ne se coche pas. */
            max_selection?: number;
        };
    };
    value: string[];
    onChange: (value: string[]) => void;
    disabled?: boolean;
}

export const QuestionMCQMulti: React.FC<QuestionMCQMultiProps> = ({
    question,
    value,
    onChange,
    disabled = false
}) => {
    const { content } = question;
    const { options, context_code, max_selection } = content;
    // Ensure selectedIds is always an array
    const selectedIds = Array.isArray(value) ? value : [];

    const handleToggle = (optionId: string) => {
        if (disabled) return;

        if (selectedIds.includes(optionId)) {
            // Remove from selection
            onChange(selectedIds.filter(id => id !== optionId));
        } else if (!max_selection || selectedIds.length < max_selection) {
            // Add to selection
            onChange([...selectedIds, optionId]);
        }
    };

    return (
        <div className="space-y-6">
            {/* Context code block if present */}
            {context_code && <ContextCode code={context_code} />}

            {/* Selection hint */}
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {max_selection
                    ? `Sélectionnez ${max_selection} réponses (${selectedIds.length} sur ${max_selection})`
                    : `Sélectionnez toutes les réponses correctes (${selectedIds.length} sélectionnée${selectedIds.length > 1 ? 's' : ''})`}
            </div>

            {/* Options as checkboxes */}
            <div className="space-y-3">
                {options.map((option) => {
                    const isSelected = selectedIds.includes(option.id);

                    return (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => handleToggle(option.id)}
                            disabled={disabled}
                            className={`
                                w-full p-4 rounded-xl text-left transition-all duration-200
                                border-2 flex items-start gap-4
                                ${isSelected
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-400'
                                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                                }
                                ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        >
                            {/* Checkbox indicator */}
                            <div className={`
                                w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                                transition-all duration-200
                                ${isSelected
                                    ? 'bg-blue-500 border-blue-500 text-white'
                                    : 'border-gray-300 dark:border-gray-600'
                                }
                            `}>
                                {isSelected && (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>

                            {/* Option content */}
                            <div className="flex-1">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Option {option.id.toUpperCase()}
                                </span>
                                {option.content_type === 'code' ? (
                                    <pre className="mt-1 font-mono text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                                        {option.value}
                                    </pre>
                                ) : (
                                    <p className="mt-1 text-gray-800 dark:text-gray-200">
                                        {option.value}
                                    </p>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestionMCQMulti;
